import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';

/**
 * Tỉ lệ việc phát sinh theo tuần — APM-MAINT-d.
 *
 * Chỉ số đặc trưng nhất của một dự án bảo trì: bao nhiêu phần trăm việc mỗi
 * tuần là việc **không ai kịp lên kế hoạch**. Nó không đo năng suất — nó đo
 * mức độ dự đoán được của công việc, thứ quyết định đội này có lập kế hoạch cho
 * bất cứ gì khác được hay không.
 *
 * ## ⚠️ Đường **đứt** ở tuần không có việc nào
 *
 * `ratio: null` chứ không phải 0, và biểu đồ phải giữ nguyên khoảng trống ấy
 * (`connectNulls` để mặc định `false`). Nối liền qua nó là vẽ ra một tuần
 * "0% phát sinh" — trông như tuần kỷ luật nhất trong kỳ, trong khi thật ra
 * không có việc nào để mà nói.
 *
 * ## Vì sao không có `asOf`
 *
 * Ngày tạo và ngày kế hoạch là thuộc tính của chính task, không dựng lại từ
 * event — nên "tuần đó có bao nhiêu việc phát sinh" cho cùng câu trả lời dù hỏi
 * hôm nay hay hôm qua. Khung vẫn nhận `asOf` để không phá luật #7, nhưng dữ
 * liệu không đổi theo nó, và ghi chú dưới biểu đồ nói ra điều đó.
 */
export function UnplannedChart({
  projectId,
  range,
}: {
  projectId: string;
  range: ReportRange;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.unplanned(projectId, range),
    queryFn: () => reportApi.unplanned(projectId, range),
  });

  const buckets = report.data?.buckets ?? [];
  const points = buckets.map((bucket) => ({
    week: bucket.week.slice(5),
    // Recharts vẽ theo số, còn `null` là khoảng trống thật — giữ nguyên nó.
    percent: bucket.ratio === null ? null : Math.round(bucket.ratio * 100),
    counted: bucket.counted,
    unplanned: bucket.unplanned,
  }));

  return (
    <ChartFrame
      title={t('reports.unplannedTitle')}
      help="unplanned"
      subtitle={t('reports.unplannedSubtitle', { days: report.data?.leadDays ?? 1 })}
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && buckets.every((bucket) => bucket.counted === 0)
          ? { title: t('reports.unplannedEmpty'), description: t('reports.unplannedEmptyHelp') }
          : null
      }
    >
      <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(value, _name, item) => {
                const row = (item as { payload?: (typeof points)[number] }).payload;
                if (typeof value !== 'number' || !row) return [t('reports.unplannedNoData'), ''];

                return [
                  t('reports.unplannedTooltip', {
                    percent: value,
                    unplanned: row.unplanned,
                    counted: row.counted,
                  }),
                  '',
                ];
              }}
            />
            <Line
              type="monotone"
              dataKey="percent"
              stroke={`var(${FLOW_VAR.inProgress})`}
              strokeWidth={2}
              dot
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-muted-foreground">{t('reports.unplannedNote')}</p>
    </ChartFrame>
  );
}
